import {ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, computed, inject, input, model, output, signal, viewChild} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {CdkTrapFocus} from '@angular/cdk/a11y';
import {Rgba, hsvaToRgba, parseColor, sameColor, toHexString, toRgbString} from './color-format';
import {ColorPanelFormat, ColorPanelState, displayColor, setStateColor, stateColor} from './color-panel-state';

/** Spectrum's palette, row by row. */
export const COLOR_PANEL_PALETTE = [
  ['#000000', '#444444', '#5b5b5b', '#999999', '#bcbcbc', '#eeeeee', '#f3f6f4', '#ffffff'],
  ['#f44336', '#744700', '#ce7e00', '#8fce00', '#2986cc', '#16537e', '#6a329f', '#c90076'],
  ['#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#cfe2f3', '#d9d2e9', '#ead1dc'],
  ['#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#9fc5e8', '#b4a7d6', '#d5a6bd'],
  ['#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6fa8dc', '#8e7cc3', '#c27ba0'],
  ['#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3d85c6', '#674ea7', '#a64d79'],
  ['#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#0b5394', '#351c75', '#741b47'],
  ['#660000', '#783f04', '#7f6000', '#274e13', '#0c343d', '#073763', '#20124d', '#4c1130'],
];

/** The palette's colours as rgb strings, which the recent row leaves out. */
export const COLOR_PANEL_PALETTE_RGB: ReadonlySet<string> = new Set(COLOR_PANEL_PALETTE.flat().map((hex) => toRgbString(parseColor(hex))));

interface Swatch {
  color: Rgba;
  rgb: string;
  title: string;
  /** Dark swatches get a white tick. */
  dark: boolean;
}

type DragTarget = 'saturation' | 'hue' | 'alpha';

/** Arrow keys on a slider, as steps along x and y. */
const ARROW_STEPS = new Map<string, [number, number]>([
  ['ArrowLeft', [-1, 0]], ['ArrowRight', [1, 0]], ['ArrowUp', [0, -1]], ['ArrowDown', [0, 1]],
]);

/** Arrow keys in the palette, as steps through its swatches; every row holds 8. */
const PALETTE_STEPS = new Map<string, number>([['ArrowLeft', -1], ['ArrowRight', 1], ['ArrowUp', -8], ['ArrowDown', 8]]);

const clamp01 = (value: number): number => Math.max(0, Math.min(1, Math.round(value * 1000) / 1000));

const toSwatch = (text: string, format: ColorPanelFormat): Swatch => {
  const color = parseColor(text);
  const max = Math.max(color.r, color.g, color.b) / 255;
  const min = Math.min(color.r, color.g, color.b) / 255;
  return {color, rgb: toRgbString(color), title: format === 'rgb' ? toRgbString(color) : toHexString(color), dark: (max + min) / 2 < 0.5};
};

/**
 * The colour panel: Spectrum's layout (palette and recent row, saturation box with a vertical hue bar, alpha bar,
 * text field, clear, Cancel and Choose), opened by ColorPanelService. The panel only moves the colour; its opener
 * decides what a Choose or a Cancel stores.
 *
 * From the keyboard it is a dialog that keeps focus inside: the palette is one Tab stop whose arrow keys move between
 * swatches, and the saturation box, hue bar and alpha bar are sliders that arrow keys move, a hundredth at a time or
 * a tenth with Shift. Enter on a slider chooses.
 */
@Component({
  selector: 'app-color-panel',
  templateUrl: './color-panel.component.html',
  styleUrls: ['./color-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, CdkTrapFocus],
})
export class ColorPanelComponent {
  readonly state = model<ColorPanelState>({hsva: {h: 0, s: 0, v: 0, a: 1}, empty: true});
  /** Shows the alpha bar; without it every move is opaque. */
  readonly alpha = input(false);
  readonly allowEmpty = input(true);
  readonly format = input<ColorPanelFormat>('hex');
  /** The picker's recent colours, oldest first. */
  readonly recent = input<string[]>([]);
  /** A palette click chooses and closes, instead of only moving the colour. */
  readonly closeOnPalette = input(false);

  readonly moved = output<Rgba | null>();
  readonly chosen = output<void>();
  readonly cancelled = output<void>();

  readonly textField = viewChild<ElementRef<HTMLInputElement>>('textField');

  readonly color = computed(() => stateColor(this.state(), this.allowEmpty()));
  readonly palette = computed(() => COLOR_PANEL_PALETTE.map((row) => row.map((hex) => toSwatch(hex, this.format()))));
  readonly recentSwatches = computed(() => [...this.recent()].reverse().map((rgb) => toSwatch(rgb, this.format())));
  readonly text = computed(() => displayColor(this.color(), this.format()));
  /** The palette's one Tab stop: the swatch of the colour, else the first. */
  readonly paletteTabStop = computed(() => {
    const swatches = [...this.palette().flat(), ...this.recentSwatches()];
    return (swatches.find((swatch) => this.isActive(swatch)) || swatches[0]).rgb;
  });
  // The sliders' values for screen readers.
  readonly hueDegrees = computed(() => Math.round(this.state().hsva.h * 360));
  readonly saturationPercent = computed(() => Math.round(this.state().hsva.s * 100));
  readonly alphaPercent = computed(() => Math.round(this.state().hsva.a * 100));
  readonly saturationText = computed(() => this.color()
    ? `Saturation ${this.saturationPercent()}%, brightness ${Math.round(this.state().hsva.v * 100)}%`
    : 'No colour');
  readonly hueColor = computed(() => toHexString(hsvaToRgba({h: this.state().hsva.h, s: 1, v: 1, a: 1})));
  readonly alphaGradient = computed(() => {
    const {r, g, b} = hsvaToRgba({...this.state().hsva, a: 1});
    return `linear-gradient(to right, ${toRgbString({r, g, b, a: 0})}, ${toHexString({r, g, b, a: 1})})`;
  });
  /** Text in the field that no format reads; Choose waits until it is fixed or replaced. */
  readonly invalidText = signal(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private drag: {target: DragTarget; rect: DOMRect; axis: 'x' | 'y' | null} | null = null;
  /** True from a drag's start until just after its end, so the click that ends it does not close the panel. */
  dragging = false;

  isActive(swatch: Swatch): boolean {
    return sameColor(swatch.color, this.color());
  }

  /** Puts focus on the palette's Tab stop, for a panel opened from the keyboard. */
  focusFirst(): void {
    this.host.nativeElement.querySelector<HTMLElement>('.color-panel__swatch[tabindex="0"]')?.focus();
  }

  paletteKey(event: KeyboardEvent): void {
    const step = PALETTE_STEPS.get(event.key);
    if (!step) {
      return;
    }
    const swatches = Array.from((event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('.color-panel__swatch'));
    const index = swatches.indexOf(event.target as HTMLElement);
    const next = swatches[index + step];
    if (index >= 0 && next) {
      event.preventDefault();
      next.focus();
    }
  }

  sliderKey(event: KeyboardEvent, target: DragTarget): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.choose();
      return;
    }
    const arrow = ARROW_STEPS.get(event.key);
    if (!arrow) {
      return;
    }
    event.preventDefault();
    const step = event.shiftKey ? 0.1 : 0.01;
    const [x, y] = arrow;
    const hsva = {...this.state().hsva};
    if (target === 'saturation') {
      hsva.s = clamp01(hsva.s + x * step);
      hsva.v = clamp01(hsva.v - y * step);
    } else if (target === 'hue') {
      // Down and Right move the handle down the bar, as a drag would.
      hsva.h = clamp01(hsva.h + (x + y) * step);
    } else {
      hsva.a = clamp01(hsva.a + (x - y) * step);
    }
    if (target !== 'alpha' && !this.alpha()) {
      hsva.a = 1;
    }
    this.update({hsva, empty: false});
  }

  pick(swatch: Swatch): void {
    this.update(setStateColor(this.state(), swatch.color, this.allowEmpty()));
    if (this.closeOnPalette()) {
      this.chosen.emit();
    }
  }

  clear(): void {
    this.update({...this.state(), empty: true});
  }

  /**
   * Reads the text field as Spectrum did, on a change or Enter: empty clears, unreadable text is flagged and changes
   * nothing. Enter only moves the colour; Choose or a click outside stores it.
   */
  applyText(value: string): boolean {
    if (value === '' && this.allowEmpty()) {
      this.update(setStateColor(this.state(), null, this.allowEmpty()));
      return true;
    }
    const color = parseColor(value);
    if (!color.ok) {
      this.invalidText.set(true);
      return false;
    }
    this.update(setStateColor(this.state(), color, this.allowEmpty()));
    return true;
  }

  onTextPaste(field: HTMLInputElement): void {
    setTimeout(() => this.applyText(field.value));
  }

  choose(): void {
    if (!this.invalidText()) {
      this.chosen.emit();
    }
  }

  dragStart(event: PointerEvent, target: DragTarget): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    const element = event.currentTarget as HTMLElement;
    element.setPointerCapture?.(event.pointerId);
    this.drag = {target, rect: element.getBoundingClientRect(), axis: null};
    this.dragging = true;
    this.dragMove(event);
  }

  dragMove(event: PointerEvent): void {
    if (!this.drag) {
      return;
    }
    const {target, rect} = this.drag;
    // Whole pixels, as the mouse events Spectrum read: a pointer event's position can be fractional.
    const x = Math.max(0, Math.min(Math.floor(event.clientX) - rect.left, rect.width));
    const y = Math.max(0, Math.min(Math.floor(event.clientY) - rect.top, rect.height));
    const hsva = {...this.state().hsva};
    if (target === 'alpha') {
      hsva.a = x / rect.width;
      if (event.shiftKey) {
        hsva.a = Math.round(hsva.a * 10) / 10;
      }
    } else {
      if (target === 'hue') {
        hsva.h = y / rect.height;
      } else {
        this.moveSaturation(hsva, x, y, rect, event.shiftKey);
      }
      if (!this.alpha()) {
        hsva.a = 1;
      }
    }
    this.update({hsva, empty: false});
  }

  dragEnd(): void {
    if (!this.drag) {
      return;
    }
    this.drag = null;
    // The click that ends a drag outside the panel comes after this.
    setTimeout(() => this.dragging = false);
  }

  /** Shift held keeps the dragger on the axis it first moved along, as Spectrum did. */
  private moveSaturation(hsva: {s: number; v: number}, x: number, y: number, rect: DOMRect, shift: boolean): void {
    if (!shift) {
      this.drag.axis = null;
    } else if (!this.drag.axis) {
      const fromX = Math.abs(x - hsva.s * rect.width);
      const fromY = Math.abs(y - (rect.height - hsva.v * rect.height));
      this.drag.axis = fromX > fromY ? 'x' : 'y';
    }
    if (this.drag.axis !== 'y') {
      hsva.s = x / rect.width;
    }
    if (this.drag.axis !== 'x') {
      hsva.v = (rect.height - y) / rect.height;
    }
  }

  private update(state: ColorPanelState): void {
    this.invalidText.set(false);
    this.state.set(state);
    // The field shows the colour again, even when it is the one already shown.
    const field = this.textField()?.nativeElement;
    if (field) {
      field.value = this.text();
    }
    this.moved.emit(this.color());
  }
}
